import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../global/supabase';
import { SUPABASE, CLIENT_ID, BRANDING } from '../config';

function AdminInsightsPanel({ userInfo }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ flags: true, unitStatus: false, trends: false, recommendations: false });
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchLatestInsight = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('admin_insights')
        .select('*')
        .eq('deployment_id', CLIENT_ID)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
      setInsight(data || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLatestInsight(); }, [fetchLatestInsight]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch(`${SUPABASE.url}/functions/v1/generate-daily-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ deployment_id: CLIENT_ID }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      await fetchLatestInsight();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!insight) return;
    try {
      const { error: updateError } = await supabase
        .from('admin_insights')
        .update({
          status: newStatus,
          reviewed_by: userInfo?.id || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', insight.id);
      if (updateError) throw updateError;
      setInsight(prev => ({ ...prev, status: newStatus, reviewed_by: userInfo?.id, reviewed_at: new Date().toISOString() }));
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('admin_insights')
        .select('id, period_start, period_end, summary, flags, status, created_at, model_used, tokens_used')
        .eq('deployment_id', CLIENT_ID)
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false });
      setHistory(data || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Loading AI Compliance Insights...
      </div>
    );
  }

  if (error && !insight) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
        Error: {error}
        <br />
        <button onClick={fetchLatestInsight} style={{ marginTop: '10px', padding: '8px 16px', background: BRANDING.primaryColor, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  if (!insight) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>No AI compliance reports generated yet.</p>
        <button onClick={handleRegenerate} disabled={regenerating} style={{ marginTop: '10px', padding: '10px 20px', background: BRANDING.primaryColor, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: regenerating ? 0.6 : 1 }}>
          {regenerating ? 'Generating...' : 'Generate First Report'}
        </button>
      </div>
    );
  }

  const flags = insight.flags || [];
  const trends = insight.trends || [];
  const unitStatus = insight.unit_status || {};
  const recommendations = insight.recommendations || [];

  const criticalCount = flags.filter(f => f.severity === 'critical').length;
  const warningCount = flags.filter(f => f.severity === 'warning').length;
  const infoCount = flags.filter(f => f.severity === 'info').length;

  const severityColors = { critical: '#d32f2f', warning: '#f57c00', info: '#1976d2' };
  const statusColors = { compliant: '#2e7d32', needs_attention: '#f57c00', non_compliant: '#d32f2f' };
  const priorityColors = { high: '#d32f2f', medium: '#f57c00', low: '#1976d2' };
  const insightStatusColors = { generated: '#1976d2', reviewed: '#2e7d32', dismissed: '#999' };

  const SectionHeader = ({ title, sectionKey, count }) => (
    <div
      onClick={() => toggleSection(sectionKey)}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', cursor: 'pointer', borderBottom: '1px solid #eee', userSelect: 'none' }}
    >
      <h4 style={{ margin: 0, color: '#333', fontSize: '15px' }}>
        {expandedSections[sectionKey] ? '▼' : '▶'} {title} {count !== undefined && <span style={{ color: '#999', fontWeight: 'normal' }}>({count})</span>}
      </h4>
    </div>
  );

  return (
    <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: BRANDING.primaryColor, color: '#fff', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px' }}>AI Compliance Insights</h3>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.8 }}>
            {new Date(insight.period_start).toLocaleDateString()} — {new Date(insight.period_end).toLocaleDateString()}
          </p>
        </div>
        <span style={{ background: insightStatusColors[insight.status] || '#999', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold' }}>
          {insight.status}
        </span>
      </div>

      {/* Severity Badge Row */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', padding: '12px 20px', background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d32f2f' }}>{criticalCount}</div>
          <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase' }}>Critical</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>{warningCount}</div>
          <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase' }}>Warning</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>{infoCount}</div>
          <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase' }}>Info</div>
        </div>
      </div>

      <div style={{ padding: '16px 20px' }}>
        {/* Executive Summary */}
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '15px' }}>Executive Summary</h4>
          <p style={{ margin: 0, color: '#555', lineHeight: 1.6, fontSize: '14px' }}>{insight.summary}</p>
        </div>

        {/* Flags */}
        <SectionHeader title="Flags" sectionKey="flags" count={flags.length} />
        {expandedSections.flags && flags.length > 0 && (
          <div style={{ padding: '12px 0' }}>
            {flags.map((flag, i) => (
              <div key={i} style={{ borderLeft: `4px solid ${severityColors[flag.severity] || '#999'}`, padding: '10px 14px', marginBottom: '8px', background: '#fafafa', borderRadius: '0 4px 4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ background: severityColors[flag.severity] || '#999', color: '#fff', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', textTransform: 'uppercase', fontWeight: 'bold' }}>{flag.severity}</span>
                  <span style={{ color: '#888', fontSize: '12px' }}>{flag.unit}</span>
                </div>
                <div style={{ fontWeight: '600', color: '#333', fontSize: '13px' }}>{flag.title}</div>
                <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>{flag.detail}</div>
              </div>
            ))}
          </div>
        )}

        {/* Unit Status */}
        <SectionHeader title="Unit Status" sectionKey="unitStatus" count={Object.keys(unitStatus).length} />
        {expandedSections.unitStatus && Object.keys(unitStatus).length > 0 && (
          <div style={{ padding: '12px 0', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f0f0f0' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Unit</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '8px', textAlign: 'center' }}>Logs</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(unitStatus).map(([name, info]) => (
                  <tr key={name} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontWeight: '600' }}>{name}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ background: statusColors[info.status] || '#999', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' }}>
                        {(info.status || '').replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '8px', textAlign: 'center' }}>{info.log_count ?? '-'}</td>
                    <td style={{ padding: '8px', color: '#666' }}>{info.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Trends */}
        <SectionHeader title="Trends" sectionKey="trends" count={trends.length} />
        {expandedSections.trends && trends.length > 0 && (
          <div style={{ padding: '12px 0' }}>
            {trends.map((trend, i) => {
              const arrow = trend.type === 'positive' ? '▲' : trend.type === 'negative' ? '▼' : '—';
              const color = trend.type === 'positive' ? '#2e7d32' : trend.type === 'negative' ? '#d32f2f' : '#666';
              return (
                <div key={i} style={{ padding: '8px 0', borderBottom: i < trends.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <span style={{ color, fontWeight: 'bold', marginRight: '6px' }}>{arrow}</span>
                  <strong style={{ color: '#333', fontSize: '13px' }}>{trend.title}</strong>
                  <span style={{ color: '#666', fontSize: '12px' }}> — {trend.detail}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* Recommendations */}
        <SectionHeader title="Recommendations" sectionKey="recommendations" count={recommendations.length} />
        {expandedSections.recommendations && recommendations.length > 0 && (
          <div style={{ padding: '12px 0' }}>
            {recommendations.map((rec, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < recommendations.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                <span style={{ background: priorityColors[rec.priority] || '#999', color: '#fff', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', textTransform: 'uppercase', marginRight: '8px', fontWeight: 'bold' }}>{rec.priority}</span>
                <strong style={{ color: '#333', fontSize: '13px' }}>{rec.title}</strong>
                <div style={{ color: '#666', fontSize: '12px', marginTop: '2px', paddingLeft: '2px' }}>{rec.detail}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        <button onClick={handleRegenerate} disabled={regenerating} style={{ padding: '8px 14px', background: BRANDING.primaryColor, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', opacity: regenerating ? 0.6 : 1 }}>
          {regenerating ? 'Generating...' : 'Regenerate Daily'}
        </button>
        {insight.status !== 'reviewed' && (
          <button onClick={() => handleStatusUpdate('reviewed')} style={{ padding: '8px 14px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
            Mark Reviewed
          </button>
        )}
        {insight.status !== 'dismissed' && (
          <button onClick={() => handleStatusUpdate('dismissed')} style={{ padding: '8px 14px', background: '#757575', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
            Dismiss
          </button>
        )}
        <button onClick={() => { setShowHistory(true); fetchHistory(); }} style={{ padding: '8px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
          View History
        </button>
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 20px', background: '#f8f9fa', borderTop: '1px solid #eee', fontSize: '11px', color: '#999', display: 'flex', justifyContent: 'space-between' }}>
        <span>Model: {insight.model_used || 'N/A'}</span>
        <span>Tokens: {insight.tokens_used || 'N/A'}</span>
        <span>Generated: {new Date(insight.created_at).toLocaleString()}</span>
      </div>

      {error && (
        <div style={{ padding: '8px 20px', background: '#fce4ec', color: '#c62828', fontSize: '12px' }}>
          {error}
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '8px', maxWidth: '700px', width: '100%', maxHeight: '80vh', overflow: 'auto', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Insight History (Last 14 Days)</h3>
              <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' }}>✕</button>
            </div>
            {historyLoading ? (
              <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
            ) : history.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666' }}>No history available.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Critical</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Warnings</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Info</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '8px', textAlign: 'center' }}>Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const hFlags = h.flags || [];
                    return (
                      <tr key={h.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px' }}>{new Date(h.created_at).toLocaleDateString()}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#d32f2f', fontWeight: 'bold' }}>{hFlags.filter(f => f.severity === 'critical').length}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#f57c00', fontWeight: 'bold' }}>{hFlags.filter(f => f.severity === 'warning').length}</td>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#1976d2', fontWeight: 'bold' }}>{hFlags.filter(f => f.severity === 'info').length}</td>
                        <td style={{ padding: '8px' }}>
                          <span style={{ background: insightStatusColors[h.status] || '#999', color: '#fff', padding: '1px 6px', borderRadius: '3px', fontSize: '10px', textTransform: 'uppercase' }}>{h.status}</span>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#999' }}>{h.tokens_used || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInsightsPanel;
